import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { MenuItem, TextField } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import Fade from '@material-ui/core/Fade'
import { makeStyles } from '@material-ui/core/styles'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useAuth } from 'Contexts/AuthProvider'
import useTranslation from 'Hooks/useTranslation'
import SearchInput from 'Components/SearchInput'
import TablePagination from 'Pages/PredictiveMaintenance/Equipment/TablePagination'
import DmsService from 'Services/DMS/dms-api.service'
import AlertToastr from 'utils/alert'

const UNIT_TYPES = ['Blender', 'Pump', 'Hydration', 'Chemical', 'AcidPump', 'MissileValveControl', 'BoostPump']
const QUERY_KEY = 'unit-setting-items'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}))

const UnitChannels = ({ uId, requestConfig }) => {
  const translation = useTranslation()
  const [search, setSearch] = useState('')

  const { data: channels = [], isLoading } = useQuery(
    ['unit-setting-channels', uId, requestConfig.headers?.CompanyId],
    async () => (await DmsService.getUnitSettingChannels(uId, requestConfig)).data,
    { refetchOnWindowFocus: false }
  )

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return channels
    return channels.filter((c) => [c.name, c.nameUser, c.unit].some((v) => v?.toLowerCase().includes(term)))
  }, [channels, search])

  return (
    <div className="unitChannelsPanel">
      <div className="unitChannelsFilters">
        <TextField
          className="unitChannelsSearch"
          variant="outlined"
          margin="dense"
          size="small"
          label={translation.dashboardSearchChannelLabel}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="unitChannelsList">
        {isLoading ? (
          <div className="unitChannelsEmpty">
            <CircularProgress size={20} />
          </div>
        ) : filtered.length ? (
          <table className="unitChannelsTable">
            <thead>
              <tr>
                <th>{translation.settingsUnitsChannelCodeHeader}</th>
                <th>{translation.settingsUnitsChannelNameHeader}</th>
                <th>{translation.settingsUnitsChannelUnitHeader}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={`${c.name}-${i}`}>
                  <td>{c.name}</td>
                  <td>{c.nameUser || '—'}</td>
                  <td>{c.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="unitChannelsEmpty">{translation.settingsUnitsNoChannelsLabel}</div>
        )}
      </div>
    </div>
  )
}

const UnitRow = ({ unit, onUpdate, expanded, onToggle, requestConfig }) => {
  const translation = useTranslation()
  const [alias, setAlias] = useState(unit.alias || '')

  useEffect(() => setAlias(unit.alias || ''), [unit.alias])

  const saveAlias = () => {
    const trimmed = alias.trim()
    if (trimmed === (unit.alias || '')) return
    onUpdate({ uId: unit.uId, type: unit.type ?? null, alias: trimmed })
  }

  // React events bubble through portals, so clicks in the Type menu reach the row too
  const onRowClick = (e) => {
    if (!e.currentTarget.contains(e.target) || e.target.closest('.MuiInputBase-root')) return
    onToggle(unit.uId)
  }

  return (
    <>
      <tr className="unitChannelsToggleRow" onClick={onRowClick}>
        <td>
          <span className={`unitChannelsCaret${expanded ? ' expanded' : ''}`} />
          {unit.name}
        </td>
        <td>{unit.uId}</td>
        <td>
          <TextField
            select
            fullWidth
            variant="outlined"
            margin="dense"
            value={unit.type || ''}
            className="unitTypeSelect"
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) =>
                value ? (
                  translation[`unitType${value}`] || value
                ) : (
                  <span className="unitTypePlaceholder">{translation.unitTypeSelectPlaceholder}</span>
                ),
            }}
            onChange={(e) => onUpdate({ uId: unit.uId, type: e.target.value, alias: unit.alias ?? null })}
          >
            {UNIT_TYPES.map((type) => (
              <MenuItem key={type} value={type} className="filterMenuItem">
                {translation[`unitType${type}`]}
              </MenuItem>
            ))}
          </TextField>
        </td>
        <td>
          <TextField
            fullWidth
            variant="outlined"
            margin="dense"
            value={alias}
            inputProps={{ maxLength: 128 }}
            onChange={(e) => setAlias(e.target.value)}
            onBlur={saveAlias}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.target.blur()
            }}
          />
        </td>
      </tr>
      {expanded ? (
        <tr className="unitChannelsRow">
          <td colSpan={4}>
            <UnitChannels uId={unit.uId} requestConfig={requestConfig} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

const UnitsManagement = () => {
  const translation = useTranslation()
  const classes = useStyles()
  const queryClient = useQueryClient()
  const { currentCompany } = useAuth()
  const [searchFilter, setSearchFilter] = useState()
  const [sortConfig, setSortConfig] = useState({ key: 'Name', descending: false })
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(15)
  const [expandedUId, setExpandedUId] = useState(null)

  const requestConfig = useMemo(
    () => (currentCompany?.id ? { headers: { CompanyId: currentCompany.id } } : {}),
    [currentCompany]
  )

  const { data, isLoading, isFetching } = useQuery(
    [QUERY_KEY, currentCompany?.id, pageNumber, pageSize, sortConfig.key, sortConfig.descending, searchFilter],
    async () =>
      (
        await DmsService.getUnitSettingItems(
          {
            pageNumber,
            pageSize,
            sortBy: sortConfig.key,
            sortDescending: sortConfig.descending,
            searchTerm: searchFilter,
          },
          requestConfig
        )
      ).data,
    { keepPreviousData: true, refetchOnWindowFocus: false }
  )

  const units = useMemo(() => data?.items ?? [], [data])
  const totalCount = data?.totalCount ?? 0
  const pageCount = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0

  useEffect(() => setPageNumber(1), [searchFilter])

  useEffect(() => {
    if (pageCount > 0 && pageNumber > pageCount) setPageNumber(pageCount)
  }, [pageCount, pageNumber])

  const updateUnit = useMutation((body) => DmsService.updateUnitSettingItem(body, requestConfig), {
    onSuccess: () => {
      AlertToastr.showAlert(translation.settingsUnitsUpdatedSuccess)
      queryClient.invalidateQueries('unique-units')
      queryClient.invalidateQueries('unique-units-search')
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.response?.data?.Message || error?.message
      if (message) AlertToastr.showErrorAlert(message)
    },
    onSettled: () => queryClient.invalidateQueries(QUERY_KEY),
  })

  const requestSort = (key) => {
    setSortConfig((prev) => ({ key, descending: prev.key === key && !prev.descending }))
    setPageNumber(1)
  }

  const getClassNamesFor = (key) => {
    if (sortConfig.key !== key) return undefined
    return sortConfig.descending ? 'hasArrow up' : 'hasArrow'
  }

  const onPrevPage = useCallback(() => {
    setPageNumber((p) => Math.max(1, p - 1))
    window.scrollTo(0, 0)
  }, [])

  const onNextPage = useCallback(() => {
    setPageNumber((p) => Math.min(pageCount || 1, p + 1))
    window.scrollTo(0, 0)
  }, [pageCount])

  const handleRowCount = useCallback((count) => {
    setPageSize(count)
    setPageNumber(1)
  }, [])

  const pagination = {
    pageNumber,
    pageSize,
    itemsCount: units.length,
    itemsTotalCount: totalCount,
    pageCount,
  }

  return (
    <div className="pageCard subscriptionCard">
      <div className="pageBtnSection pt0 mt5 unitSettingsPageToolbar">
        <div className="searchHolder">
          <div className="inputHolder" style={{ display: 'flex', verticalAlign: 'center' }}>
            <SearchInput search={searchFilter} onChange={setSearchFilter} />
          </div>
        </div>
      </div>
      <div className="tableHolder" style={{ position: 'relative', minHeight: 120 }}>
        <table className="unitSettingsTable">
          <thead>
            <tr className="headingRow">
              <th onClick={() => requestSort('Name')} className={getClassNamesFor('Name')} style={{ width: '25%' }}>
                {translation.settingsUnitsNameHeader}
              </th>
              <th onClick={() => requestSort('UId')} className={getClassNamesFor('UId')} style={{ width: '25%' }}>
                UID
              </th>
              <th onClick={() => requestSort('Type')} className={getClassNamesFor('Type')} style={{ width: '25%' }}>
                {translation.unitTypeColumnLabel}
              </th>
              <th onClick={() => requestSort('Alias')} className={getClassNamesFor('Alias')} style={{ width: '25%' }}>
                {translation.settingsUnitsAliasHeader}
              </th>
            </tr>
          </thead>
          <tbody>
            {units.map((unit) => (
              <UnitRow
                key={unit.uId}
                unit={unit}
                onUpdate={updateUnit.mutate}
                expanded={expandedUId === unit.uId}
                onToggle={(uId) => setExpandedUId((prev) => (prev === uId ? null : uId))}
                requestConfig={requestConfig}
              />
            ))}
            {!units.length && !isLoading ? (
              <tr>
                <td colSpan={4} className="text-center">{translation.dashboardNoUnitsLabel}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <div className={classes.root}>
          <Fade in={isLoading || isFetching} unmountOnExit>
            <CircularProgress />
          </Fade>
        </div>
      </div>
      {totalCount > 0 && (
        <TablePagination
          pagination={pagination}
          onPrevPage={onPrevPage}
          onNextPage={onNextPage}
          handleRowCount={handleRowCount}
        />
      )}
    </div>
  )
}

export default UnitsManagement
